import React from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from '@/utils/formatters';
import type { RenewalForecastPeriod } from '@/types';

/**
 * Series colours, validated with the dataviz palette checks (CVD and normal-vision separation,
 * contrast on the light surface): the dashboard's bar blue and the notice-window tile orange.
 */
export const AUTO_RENEW_COLOR = '#2563eb';
export const DECISION_NEEDED_COLOR = '#ed6c02';

interface RenewalForecastChartProps {
  periods: RenewalForecastPeriod[];
  /** The selected period's key, or null */
  selectedKey: string | null;
  /** Called with the clicked period's key, or null when the selected period is clicked again */
  onSelect: (key: string | null) => void;
  height?: number;
}

/** Narrow a Recharts click payload to the period it was drawn from (never index by position). */
function periodKeyOf(payload: unknown): string | null {
  if (payload && typeof payload === 'object' && 'key' in payload && typeof (payload as { key: unknown }).key === 'string') {
    return (payload as { key: string }).key;
  }
  return null;
}

interface TooltipPayloadEntry {
  payload?: unknown;
}

const ForecastTooltip: React.FC<{ active?: boolean; payload?: TooltipPayloadEntry[] }> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const period = payload[0].payload as RenewalForecastPeriod | undefined;
  if (!period || typeof period.key !== 'string') return null;
  return (
    <div className="rounded-md border border-border bg-white px-3 py-2 text-xs shadow-md">
      <p className="font-semibold text-grey-800 mb-1">
        {period.label}: {period.contractCount} {period.contractCount === 1 ? 'contract' : 'contracts'}
      </p>
      <p className="text-grey-700">
        <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle" style={{ backgroundColor: AUTO_RENEW_COLOR }} />
        Auto-renews: {formatCurrency(period.autoRenewAmount)} ({period.autoRenewCount})
      </p>
      <p className="text-grey-700">
        <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle" style={{ backgroundColor: DECISION_NEEDED_COLOR }} />
        Decision needed: {formatCurrency(period.decisionNeededAmount)} ({period.decisionNeededCount})
      </p>
      <p className="text-grey-800 font-semibold mt-1">Total: {formatCurrency(period.totalAmount)}</p>
    </div>
  );
};

/**
 * Stacked bar chart of projected renewal amounts per fiscal period: auto-renewing contracts
 * below, contracts needing a decision on top. Each bar is a click target that selects its period;
 * the segments carry `data-period-key` so a period can be found by key rather than position.
 */
export const RenewalForecastChart: React.FC<RenewalForecastChartProps> = ({
  periods,
  selectedKey,
  onSelect,
  height = 300,
}) => {
  const handleClick = (item: { payload?: unknown }) => {
    const key = periodKeyOf(item.payload);
    if (key === null) return;
    onSelect(key === selectedKey ? null : key);
  };

  const opacityFor = (period: RenewalForecastPeriod): number =>
    selectedKey === null || selectedKey === period.key ? 1 : 0.45;

  return (
    // contain-inline-size: the fixed-width wrapper Recharts renders must not set the page's
    // minimum width, or a phone-width layout grows to the chart's first measurement
    <div data-testid="renewal-forecast-chart" className="w-full contain-inline-size">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={periods} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="25%">
          <CartesianGrid strokeDasharray="3 3" stroke="#DFE3E8" vertical={false} />
          {/* preserveStartEnd lets Recharts drop ticks that would collide at narrow widths */}
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12 }}
            stroke="#919EAB"
            interval="preserveStartEnd"
            angle={periods.length > 12 ? -45 : 0}
            textAnchor={periods.length > 12 ? 'end' : 'middle'}
            height={periods.length > 12 ? 56 : 30}
          />
          <YAxis tick={{ fontSize: 12 }} stroke="#919EAB" tickFormatter={(v) => `$${(Number(v) / 1000).toFixed(0)}k`} width={56} />
          <RechartsTooltip content={<ForecastTooltip />} cursor={{ fill: 'rgba(145, 158, 171, 0.12)' }} />
          <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
          <Bar
            dataKey="autoRenewAmount"
            name="Auto-renews"
            stackId="renewals"
            fill={AUTO_RENEW_COLOR}
            stroke="#fff"
            strokeWidth={2}
            maxBarSize={48}
            cursor="pointer"
            onClick={handleClick}
            isAnimationActive={false}
          >
            {periods.map((period) => (
              <Cell key={period.key} data-period-key={period.key} fillOpacity={opacityFor(period)} />
            ))}
          </Bar>
          <Bar
            dataKey="decisionNeededAmount"
            name="Decision needed"
            stackId="renewals"
            fill={DECISION_NEEDED_COLOR}
            stroke="#fff"
            strokeWidth={2}
            radius={[4, 4, 0, 0]}
            maxBarSize={48}
            cursor="pointer"
            onClick={handleClick}
            isAnimationActive={false}
          >
            {periods.map((period) => (
              <Cell key={period.key} data-period-key={period.key} fillOpacity={opacityFor(period)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
